import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import FormLabel from '@material-ui/core/FormLabel';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import filesize from 'filesize';
import {
  DotNetValidationProblemDetailsTyped,
  VseApplicationEntity,
} from '../types';
import * as VseValidation from '../utils/validate';

export type VseApplFormDocumentEventHandler = (documentType: string, file: File, requestId?: string, fileID?: string) => void;

export interface VseApplFormDocumentUploadProps extends React.HTMLAttributes<HTMLElement> {
  requestId?: string,
  fileID?: string,
  documentType: string,
  title: string,
  required: boolean,
  uploading: boolean,
  uploaded: boolean,
  accept: Array<string>,
  onDocumentCancel: VseApplFormDocumentEventHandler,
  onDocumentUpload: VseApplFormDocumentEventHandler,
  onValidationProblem: (valProblems: DotNetValidationProblemDetailsTyped<VseApplicationEntity>) => void,
}

const useStyles = makeStyles({
  hidden: {
    display: 'none',
  },
});

export function VseApplFormDocumentUpload(props: VseApplFormDocumentUploadProps) {
  function handleChange(ev: React.ChangeEvent<HTMLInputElement>, setFile: React.Dispatch<React.SetStateAction<File | undefined>>) {
    let valProblems: DotNetValidationProblemDetailsTyped<VseApplicationEntity> | undefined = VseValidation.createValidationProblemDetails();
    const files: FileList | null = ev.target.files;

    if (files && files.length === 1) {
      const file: File = files[0];
      if (!props.accept.find((type) => type === file.type)) {
        VseValidation.appendErrorTo(valProblems, `document[${props.title}]`, 'Invalid document type');
      }
      // Max size: 2MB
      if (file.size > (2 * 1024 * 1024)) {
        VseValidation.appendErrorTo(valProblems, `document[${props.title}]`, 'Document size is larger than 2Mb');
      }

      if (VseValidation.isValid(valProblems)) {
        setFile(file);
        props.onDocumentUpload(props.documentType, file);
      } else {
        props.onValidationProblem(valProblems);
      }
    }
  }

  function handleCancel(file: File | undefined, setFile: React.Dispatch<React.SetStateAction<File | undefined>>) {
    if (file) {
      props.onDocumentCancel(props.documentType, file, props.requestId);
      setFile(undefined);
    }
  }

  function renderFile(file: File | undefined) {
    return file ? (
      <React.Fragment>
        <Typography>{file.name}</Typography>
        <Typography>{filesize(file.size)}</Typography>
      </React.Fragment>
    ) : <React.Fragment />;
  }

  const classes = useStyles();
  const title = (
    <FormLabel color='secondary' required={props.required}>{props.title}</FormLabel>
  );
  const [file, setFile] = React.useState<File>();

  return (
    <Card>
      <CardHeader
        title={title}
        disableTypography={true}
      />
      <CardContent>
        {renderFile(file)}
        {props.uploading ? (
          <LinearProgress color="secondary" />
        ): undefined}
      </CardContent>
      <CardActions>
        <label
          className={clsx(file !== undefined && classes.hidden)}
        >
          <input
            type="file"
            accept={props.accept.join(", ")}
            onChange={
              (ev) => handleChange(ev, setFile)
            }
            hidden
          />
          <Button
            variant="contained"
            color="primary"
            component="span"
          >Choose file</Button>
        </label>
        <Button
          className={clsx(file === undefined && classes.hidden)}
          variant="contained"
          color="default"
          onClick={() => handleCancel(file, setFile)}
        >Cancel</Button>
      </CardActions>
    </Card>
  );
}
